import { z } from 'zod';
import { jsonValueSchema } from '../flow/json-pointer';
import {
  WEEKLY_PLAN_MAX_SELECTION,
  WEEKLY_PLAN_MIN_SELECTION,
  WEEKLY_PLAN_SUGGESTION_COUNT,
  weeklyPlanSuggestionSchema,
} from '../weekly-plan/schema';

export const MEMBER_PLAN_KINDS = ['foundation', 'weekly'] as const;
export const MEMBER_PLAN_STATUSES = ['active', 'completed', 'ended_early'] as const;
export const PLAN_ITEM_TYPES = ['theme', 'action'] as const;
export const PLAN_ITEM_ORIGINS = ['ai', 'member', 'care_plan'] as const;
export const PLAN_REVIEW_STATUSES = ['in_progress', 'completed'] as const;
export const PLAN_REVIEW_ELIGIBILITY_REASONS = [
  'conversation_requirement',
  'home_arrival_timeout',
  'scheduled_end',
] as const;
export const PLAN_REVIEW_ENTRY_TYPES = ['answer', 'observation', 'quote', 'suggestion'] as const;

export const memberPlanKindSchema = z.enum(MEMBER_PLAN_KINDS);
export const memberPlanStatusSchema = z.enum(MEMBER_PLAN_STATUSES);
export const planItemTypeSchema = z.enum(PLAN_ITEM_TYPES);
export const planItemOriginSchema = z.enum(PLAN_ITEM_ORIGINS);
export const planReviewStatusSchema = z.enum(PLAN_REVIEW_STATUSES);
export const planReviewEligibilityReasonSchema = z.enum(PLAN_REVIEW_ELIGIBILITY_REASONS);
export const planReviewEntryTypeSchema = z.enum(PLAN_REVIEW_ENTRY_TYPES);

/** JSONB payloads reject undefined, functions, non-finite numbers, and cycles at the Zod boundary. */
export const planItemConfigSchema = z.record(z.string(), jsonValueSchema);
export const planReviewProgressSnapshotSchema = z.record(z.string(), jsonValueSchema);
export const planReviewEntryContentSchema = z.record(z.string(), jsonValueSchema);

export const weeklyQuantityLimitConfigSchema = z
  .object({ value: z.number().int().nonnegative() })
  .strict();
export const WEEKLY_QUANTITY_LIMIT_CAPABILITY_KEY = 'weekly_quantity_limit';

/**
 * How many completed Sona conversations a recurring weekly plan asks for. A
 * floor, not a target: meeting it unlocks nothing and the count keeps rising.
 * Server-owned until the standard conversation action becomes a registered plan
 * item (ADR-116 Rule 9); the home projection reports it beside the count so no
 * client ever hardcodes the number.
 */
export const WEEKLY_PLAN_CONVERSATION_FLOOR = 2;
export const WEEKLY_QUANTITY_LIMIT_CAPABILITY_VERSION = 1;

export const aiThemeConfigSchema = z
  .object({
    suggestionId: z.string().min(1),
    rank: weeklyPlanSuggestionSchema.shape.rank,
    category: weeklyPlanSuggestionSchema.shape.category,
    title: weeklyPlanSuggestionSchema.shape.title,
    blurb: weeklyPlanSuggestionSchema.shape.blurb,
  })
  .strict();

/**
 * A suggestion as retained on a completed review. Version-one evidence predates
 * program association, so a retained row without `memberProgramId` is a
 * pre-association suggestion and reads as cross-program; the live suggestion
 * contract keeps the key required-nullable so new writes can never omit it.
 */
const retainedSuggestionV1Schema = weeklyPlanSuggestionSchema
  .extend({ memberProgramId: z.string().uuid().nullable().optional() })
  .strict();

export const planReviewSuggestionEvidenceV1Schema = z
  .object({
    requestKey: z.string().uuid(),
    offeredSuggestions: z.array(retainedSuggestionV1Schema).length(WEEKLY_PLAN_SUGGESTION_COUNT),
    selectedSuggestionIds: z
      .array(z.string().min(1))
      .min(WEEKLY_PLAN_MIN_SELECTION)
      .max(WEEKLY_PLAN_MAX_SELECTION),
  })
  .strict()
  .superRefine((evidence, context) => {
    const offeredById = new Map(
      evidence.offeredSuggestions.map((suggestion) => [suggestion.id, suggestion]),
    );
    if (offeredById.size !== evidence.offeredSuggestions.length) {
      context.addIssue({
        code: 'custom',
        path: ['offeredSuggestions'],
        message: 'offered suggestion ids must be unique',
      });
    }
    if (new Set(evidence.selectedSuggestionIds).size !== evidence.selectedSuggestionIds.length) {
      context.addIssue({
        code: 'custom',
        path: ['selectedSuggestionIds'],
        message: 'selected suggestion ids must be unique',
      });
    }
    const selected = evidence.selectedSuggestionIds.map((id) => offeredById.get(id));
    if (selected.some((suggestion) => suggestion === undefined)) {
      context.addIssue({
        code: 'custom',
        path: ['selectedSuggestionIds'],
        message: 'selected suggestion ids must belong to the offered snapshot',
      });
      return;
    }
    const ranks = selected.map((suggestion) => suggestion?.rank ?? 0);
    if (
      ranks.some((rank, index) => {
        const previousRank = ranks[index - 1];
        return previousRank !== undefined && rank <= previousRank;
      })
    ) {
      context.addIssue({
        code: 'custom',
        path: ['selectedSuggestionIds'],
        message: 'selected suggestion ids must be in canonical rank order',
      });
    }
  });

export type MemberPlanKind = z.infer<typeof memberPlanKindSchema>;
export type MemberPlanStatus = z.infer<typeof memberPlanStatusSchema>;
export type PlanItemType = z.infer<typeof planItemTypeSchema>;
export type PlanItemOrigin = z.infer<typeof planItemOriginSchema>;
export type PlanReviewStatus = z.infer<typeof planReviewStatusSchema>;
export type PlanReviewEligibilityReason = z.infer<typeof planReviewEligibilityReasonSchema>;
export type PlanReviewEntryType = z.infer<typeof planReviewEntryTypeSchema>;
export type PlanItemConfig = z.infer<typeof planItemConfigSchema>;
export type PlanReviewProgressSnapshot = z.infer<typeof planReviewProgressSnapshotSchema>;
export type PlanReviewEntryContent = z.infer<typeof planReviewEntryContentSchema>;
export type AiThemeConfig = z.infer<typeof aiThemeConfigSchema>;
export type PlanReviewSuggestionEvidenceV1 = z.infer<typeof planReviewSuggestionEvidenceV1Schema>;
export type StoredPlanReviewEntryContent = PlanReviewEntryContent | PlanReviewSuggestionEvidenceV1;
